import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import { Email as EmailIcon, Send as SendIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';

interface EmailServiceStatus {
  configured: boolean;
  provider: string;
}

const AdminEmailService: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<EmailServiceStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [sending, setSending] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    checkPlatformAdminAccess();
  }, []);

  const checkPlatformAdminAccess = async () => {
    try {
      setCheckingAccess(true);
      const profile = await apiService.getProfile();
      if (profile.data?.user?.systemRole !== 'PLATFORM_ADMIN') {
        navigate('/dashboard');
        return;
      }
      fetchStatus();
    } catch {
      setError('Failed to verify access. Please try again.');
      navigate('/dashboard');
    } finally {
      setCheckingAccess(false);
    }
  };

  const fetchStatus = async () => {
    try {
      setLoadingStatus(true);
      setError(null);
      const res = await apiService.getEmailServiceStatus();
      setStatus(res.data || null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load email service status');
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleSendTest = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Please enter an email address');
      return;
    }

    try {
      setSending(true);
      setError(null);
      setSuccess(null);
      await apiService.sendTestEmail(trimmed);
      setSuccess(`Test email sent to ${trimmed}. Check the inbox (and spam folder) to verify delivery.`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send test email');
    } finally {
      setSending(false);
    }
  };

  if (checkingAccess) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Email service
        </Typography>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Send a test email to verify the configured provider and troubleshoot delivery (e.g. spam filters) with clients.
      </Typography>

      {loadingStatus ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={32} />
        </Box>
      ) : status && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Provider
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {status.configured ? (
                <strong>{status.provider}</strong>
              ) : (
                <>Not configured (set MAILJET_API_KEY and MAILJET_SECRET_KEY)</>
              )}
            </Typography>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Send test email
          </Typography>
          <Box display="flex" flexDirection="column" gap={2} maxWidth={400}>
            <TextField
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              disabled={!status?.configured}
              InputProps={{
                startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />
            <Button
              variant="contained"
              startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
              onClick={handleSendTest}
              disabled={!status?.configured || sending}
            >
              {sending ? 'Sending…' : 'Send test mail'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
    </Box>
  );
};

export default AdminEmailService;
